/**
 * Custom hook for tracking reading sessions with idle detection
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { api, API_BASE_URL } from '../config/api';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const IDLE_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute
const PAGE_UPDATE_DEBOUNCE_MS = 2000; // Debounce page updates by 2 seconds

export interface SessionStats {
  total_time_seconds: number;
  avg_session_duration_seconds: number;
  pages_per_session: number;
  sessions_count: number;
}

export function useReadingSession(bookId: string, currentPage: number, isReady: boolean = false) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [stats, setStats] = useState<SessionStats | null>(null);

  // Refs for tracking state without triggering re-renders
  const sessionIdRef = useRef<string | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const pagesVisitedRef = useRef<Set<number>>(new Set());
  const isIdleRef = useRef<boolean>(false);
  const currentPageRef = useRef<number>(currentPage);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pageUpdateDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const isStartingRef = useRef<boolean>(false); // Prevent concurrent session creation

  // Per-page dwell time tracking. pageEnteredAtRef holds the wall-clock
  // timestamp at which the user last became active on currentPageRef. When
  // null, tracking is paused (tab hidden / idle / no session yet).
  // pageTimeMsRef accumulates MILLISECONDS per page since the last successful
  // network send. We track in ms (not seconds) so that sub-second navigations
  // accumulate properly across multiple flushes — flooring to seconds at every
  // flush would silently drop any visit shorter than a second. We convert to
  // integer seconds only at ship time via collectDeltasForShipping().
  const pageEnteredAtRef = useRef<number | null>(null);
  const pageTimeMsRef = useRef<Record<string, number>>({});

  // Move elapsed milliseconds on currentPage into the accumulator and reset
  // the page-entered timestamp. Safe to call repeatedly — within the same
  // instant it's a no-op.
  const flushPageTime = useCallback(() => {
    if (pageEnteredAtRef.current === null) return;
    const now = Date.now();
    const deltaMs = Math.max(0, now - pageEnteredAtRef.current);
    if (deltaMs > 0) {
      const key = String(currentPageRef.current);
      pageTimeMsRef.current[key] = (pageTimeMsRef.current[key] || 0) + deltaMs;
    }
    pageEnteredAtRef.current = now;
  }, []);

  // Convert the ms accumulator into integer-second deltas for shipping.
  // Keeps the sub-second remainder in pageTimeMsRef so accumulation across
  // multiple flushes doesn't lose fractional seconds (the bug that dropped
  // page 412 entirely on rapid back-to-back navigation).
  // Example: 600ms + 700ms + 900ms = 2200ms → ship "2", retain 200ms.
  const collectDeltasForShipping = useCallback((): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const key of Object.keys(pageTimeMsRef.current)) {
      const totalMs = pageTimeMsRef.current[key];
      const seconds = Math.floor(totalMs / 1000);
      if (seconds > 0) {
        out[key] = seconds;
        pageTimeMsRef.current[key] = totalMs - seconds * 1000;   // keep remainder
      }
    }
    return out;
  }, []);

  // Record user activity
  const recordActivity = useCallback(() => {
    const wasIdle = isIdleRef.current;
    lastActivityRef.current = Date.now();

    // If was idle, start a new session
    if (wasIdle && bookId) {
      console.log('[ReadingSession] User returned from idle, starting new session at page', currentPageRef.current);
      isIdleRef.current = false;
      startSession();
    }
  }, [bookId]);

  // Start a new reading session
  const startSession = useCallback(async () => {
    if (!bookId) return;

    // Prevent duplicate session creation
    if (sessionIdRef.current) {
      console.log('[ReadingSession] Session already exists, skipping startSession');
      return;
    }

    // Prevent concurrent session creation (race condition from React Strict Mode)
    if (isStartingRef.current) {
      console.log('[ReadingSession] Session creation already in progress, skipping');
      return;
    }

    isStartingRef.current = true;

    // CRITICAL: Cancel any pending debounced updates that might use stale session ID
    if (pageUpdateDebounceRef.current) {
      clearTimeout(pageUpdateDebounceRef.current);
      pageUpdateDebounceRef.current = null;
      console.log('[ReadingSession] Cleared pending debounced update before new session');
    }

    try {
      const response = await api.post(`/api/books/${bookId}/sessions`, {
        start_page: currentPageRef.current,
        device_info: navigator.userAgent
      });

      sessionIdRef.current = response.data.session_id;
      setSessionId(response.data.session_id);
      setIsActive(true);
      isIdleRef.current = false;
      pagesVisitedRef.current = new Set([currentPageRef.current]);
      lastActivityRef.current = Date.now();

      // Begin dwell-time tracking on the start page. Any deltas accumulated
      // before this point belong to a prior session and would be misattributed
      // if shipped now.
      pageEnteredAtRef.current = Date.now();
      pageTimeMsRef.current = {};

      console.log('[ReadingSession] Started session:', response.data.session_id, 'at page', currentPageRef.current);
    } catch (err) {
      console.error('[ReadingSession] Failed to start session:', err);
    } finally {
      isStartingRef.current = false;
    }
  }, [bookId]);

  // End the current session
  const endSession = useCallback(() => {
    if (!sessionIdRef.current || !bookId) return;

    // Cancel any pending debounced page updates to prevent stale session calls
    if (pageUpdateDebounceRef.current) {
      clearTimeout(pageUpdateDebounceRef.current);
      pageUpdateDebounceRef.current = null;
    }

    // Capture any unsent dwell time on the current page, then collect the
    // ms accumulator into integer-second deltas for shipping. The end is
    // fire-and-forget via sendBeacon so we don't wait for a response —
    // anything left in the ms remainder is lost when the page goes away.
    flushPageTime();
    const deltasToSend = collectDeltasForShipping();

    // Use sendBeacon for reliable delivery on page close
    const url = `${API_BASE_URL}/api/books/${bookId}/sessions/${sessionIdRef.current}/end`;
    const data = JSON.stringify({
      end_page: currentPageRef.current,
      pages_viewed: Array.from(pagesVisitedRef.current),
      page_time_seconds: deltasToSend
    });

    // Must use Blob with application/json content-type for FastAPI to parse
    const blob = new Blob([data], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
    console.log('[ReadingSession] Ended session:', sessionIdRef.current, 'deltas:', JSON.stringify(deltasToSend));

    sessionIdRef.current = null;
    setSessionId(null);
    setIsActive(false);
    pageEnteredAtRef.current = null;
    pageTimeMsRef.current = {};
  }, [bookId, flushPageTime, collectDeltasForShipping]);

  // Send heartbeat to keep session alive
  const sendHeartbeat = useCallback(async () => {
    if (!sessionIdRef.current || !bookId || isIdleRef.current) return;

    // Flush elapsed dwell time on currentPage, then move integer-second deltas
    // OUT of the ms accumulator (sub-second remainder stays for next time).
    // If the network call fails we restore them; if it succeeds or the session
    // is dead, we leave the accumulator alone.
    flushPageTime();
    const deltasToSend = collectDeltasForShipping();

    try {
      const response = await api.post(
        `/api/books/${bookId}/sessions/${sessionIdRef.current}/heartbeat`,
        {
          current_page: currentPageRef.current,
          pages_viewed: Array.from(pagesVisitedRef.current),
          page_time_seconds: deltasToSend
        }
      );

      if (response.data.status === 'expired') {
        // Session expired server-side — drop deltas (orphaned to a dead session).
        console.log('[ReadingSession] Scheduled heartbeat: session expired server-side, clearing local state');
        sessionIdRef.current = null;
        setSessionId(null);
        setIsActive(false);
      }
      // success: data is on the server, accumulator already has the remainder
    } catch (err) {
      // Network error: restore deltas so the next heartbeat retries them.
      // Convert seconds back to ms when restoring.
      console.error('[ReadingSession] Heartbeat failed:', err);
      for (const key of Object.keys(deltasToSend)) {
        pageTimeMsRef.current[key] = (pageTimeMsRef.current[key] || 0) + deltasToSend[key] * 1000;
      }
    }
  }, [bookId, flushPageTime, collectDeltasForShipping]);

  // Check for idle timeout
  const checkIdle = useCallback(() => {
    const idleTime = Date.now() - lastActivityRef.current;
    const idleMinutes = Math.floor(idleTime / 60000);
    const idleSeconds = Math.floor((idleTime % 60000) / 1000);

    console.log(`[ReadingSession] Idle check: ${idleMinutes}m ${idleSeconds}s idle, threshold: 30m, isIdle: ${isIdleRef.current}, hasSession: ${!!sessionIdRef.current}`);

    if (idleTime >= IDLE_TIMEOUT_MS && !isIdleRef.current && sessionIdRef.current) {
      console.log('[ReadingSession] User went idle, ending session');
      console.log('[ReadingSession] Final pages visited:', Array.from(pagesVisitedRef.current));
      console.log('[ReadingSession] End page:', currentPageRef.current);
      isIdleRef.current = true;
      endSession();
    }
  }, [endSession]);

  // Fetch session stats
  const fetchStats = useCallback(async () => {
    if (!bookId) return;

    try {
      const response = await api.get(`/api/books/${bookId}/sessions/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('[ReadingSession] Failed to fetch stats:', err);
    }
  }, [bookId]);

  // Track page changes with debounced server update
  useEffect(() => {
    // Flush time spent on the PREVIOUS page (currentPageRef still holds it)
    // BEFORE swapping currentPageRef to the new page. Without this, the
    // previous page's accumulated dwell time would get misattributed to
    // the new page on the next flush.
    flushPageTime();

    // Update local tracking
    currentPageRef.current = currentPage;
    pagesVisitedRef.current.add(currentPage);
    lastActivityRef.current = Date.now();
    // Restart the dwell-time clock on the new page (only if a session is
    // active and tab is visible — flushPageTime is paused otherwise).
    if (sessionIdRef.current && !isIdleRef.current && !document.hidden) {
      pageEnteredAtRef.current = Date.now();
    }

    console.log(`[ReadingSession] Page changed to ${currentPage}, visited: [${Array.from(pagesVisitedRef.current).join(', ')}]`);

    // Clear any pending debounced update
    if (pageUpdateDebounceRef.current) {
      clearTimeout(pageUpdateDebounceRef.current);
    }

    // Debounce server update by 2 seconds
    pageUpdateDebounceRef.current = setTimeout(async () => {
      // Capture current session ID to check for staleness
      const currentSessionId = sessionIdRef.current;

      if (currentSessionId && bookId && !isIdleRef.current) {
        // Flush + collect integer-second deltas (sub-second remainder stays
        // in pageTimeMsRef). On network failure we restore them by adding
        // the seconds back as ms; on success or session-dead, we leave the
        // accumulator alone (which already has the remainder).
        flushPageTime();
        const deltasToSend = collectDeltasForShipping();

        try {
          const response = await api.post(`/api/books/${bookId}/sessions/${currentSessionId}/heartbeat`, {
            current_page: currentPage,
            pages_viewed: Array.from(pagesVisitedRef.current),
            page_time_seconds: deltasToSend
          });

          if (response.data.status === 'expired') {
            console.log('[ReadingSession] Debounced update: session expired, will start new on next activity');
            sessionIdRef.current = null;
            setSessionId(null);
            setIsActive(false);
          } else {
            console.log(`[ReadingSession] Debounced update sent: page ${currentPage}, pages: [${Array.from(pagesVisitedRef.current).join(', ')}], deltas: ${JSON.stringify(deltasToSend)}`);
          }
        } catch (err: any) {
          if (err?.response?.status === 404) {
            console.log('[ReadingSession] Debounced update: session not found (already ended), clearing local state');
            sessionIdRef.current = null;
            setSessionId(null);
            setIsActive(false);
            // session is dead — orphan deltas, do nothing
          } else {
            // Network error — restore so next send retries
            console.error('[ReadingSession] Debounced update failed:', err);
            for (const key of Object.keys(deltasToSend)) {
              pageTimeMsRef.current[key] = (pageTimeMsRef.current[key] || 0) + deltasToSend[key] * 1000;
            }
          }
        }
      }
    }, PAGE_UPDATE_DEBOUNCE_MS);

    return () => {
      if (pageUpdateDebounceRef.current) {
        clearTimeout(pageUpdateDebounceRef.current);
      }
    };
  }, [currentPage, bookId, flushPageTime, collectDeltasForShipping]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      window.addEventListener(event, recordActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, recordActivity);
      });
    };
  }, [recordActivity]);

  // Set up heartbeat interval
  useEffect(() => {
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [sendHeartbeat]);

  // Set up idle check interval
  useEffect(() => {
    idleCheckIntervalRef.current = setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS);

    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [checkIdle]);

  // Start session on mount, end on unmount
  // Only start when isReady is true (book data has loaded)
  useEffect(() => {
    if (bookId && isReady) {
      console.log('[ReadingSession] isReady=true, starting session');
      startSession();
    }

    return () => {
      endSession();
    };
  }, [bookId, isReady]); // Start session when ready

  // Handle page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      endSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [endSession]);

  // Pause dwell-time tracking while the tab is hidden (alt-tab, lock screen,
  // background). Without this, "tab open in background" looks identical to
  // "actively reading" — and a student who switches to look up answers gets
  // false reading time attributed.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        // Capture elapsed before pausing, then mark paused.
        flushPageTime();
        pageEnteredAtRef.current = null;
      } else if (sessionIdRef.current && !isIdleRef.current) {
        // Resume tracking on the current page.
        pageEnteredAtRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [flushPageTime]);

  return {
    sessionId,
    isActive,
    stats,
    recordActivity,
    fetchStats,
    pagesVisited: Array.from(pagesVisitedRef.current)
  };
}
